/**
 * Thumbnail & High-Res Renderer Module
 *
 * Renders parcel images on-demand using the existing WebAssembly infrastructure.
 * Uses the worker pool for parallel rendering with the parcel's saved rendering config.
 *
 * - Thumbnails (128px): single worker call, used in collection/gallery
 * - High-res (e.g. 2400×3200): column-split parallel rendering, used at merch order time
 */
import { getMapInstance } from "./index";
import type { Pool, FunctionThread } from "threads";
import type { Parcel } from "./store";
import type { ComplexBounds, WorkerRequest, WorkerResponse } from "./MandelbrotMap";

type TaskThread = FunctionThread<[WorkerRequest], WorkerResponse>;

// Default thumbnail size (square)
const DEFAULT_THUMBNAIL_SIZE = 128;

// Aspect ratio for viewport (16:9)
const ASPECT_RATIO = 9 / 16;

// Base viewport width at zoom level 3 (default view showing full Mandelbrot set)
// At zoom 3, the viewport is approximately 3.5 complex units wide
const BASE_VIEWPORT_WIDTH = 3.5;
const BASE_ZOOM = 3;

// Column-split threshold: images wider than this use parallel column rendering
const COLUMN_SPLIT_THRESHOLD = 512;
const NUM_COLUMNS = 24;

/**
 * Calculate complex plane bounds from parcel center, zoom, and image dimensions.
 *
 * Zoom is exponential (Leaflet style): each zoom level halves the viewport size.
 * The aspect ratio of the bounds matches the image dimensions so the fractal
 * isn't stretched.
 */
function calculateBounds(
  re: number,
  im: number,
  zoom: number,
  imageWidth: number,
  imageHeight: number,
): ComplexBounds {
  const viewportWidth = BASE_VIEWPORT_WIDTH / Math.pow(2, zoom - BASE_ZOOM);
  const viewportHeight = viewportWidth * (imageHeight / imageWidth);

  return {
    reMin: re - viewportWidth / 2,
    reMax: re + viewportWidth / 2,
    imMin: im - viewportHeight / 2,
    imMax: im + viewportHeight / 2,
  };
}

/**
 * Build a worker request for a region of the complex plane using a parcel's config
 */
function buildWorkerRequest(
  parcel: Parcel,
  bounds: ComplexBounds,
  imageWidth: number,
  imageHeight: number,
): WorkerRequest {
  return {
    type: "calculate" as const,
    payload: {
      bounds,
      imageWidth,
      imageHeight,
      iterations: parcel.iterations,
      exponent: 2,
      colorScheme: parcel.colorScheme,
      reverseColors: parcel.reverseColors,
      lightenAmount: parcel.lightenAmount,
      saturateAmount: parcel.saturateAmount,
      shiftHueAmount: parcel.shiftHueAmount,
      colorSpace: parcel.colorSpace,
      smoothColoring: parcel.smoothColoring,
      paletteMinIter: parcel.paletteMinIter,
      paletteMaxIter: parcel.paletteMaxIter,
    },
  };
}

/**
 * Render a single chunk via the worker pool and return a canvas
 */
function renderChunk(
  pool: Pool<TaskThread>,
  request: WorkerRequest,
  width: number,
  height: number,
): Promise<HTMLCanvasElement> {
  return new Promise<HTMLCanvasElement>((resolve, reject) => {
    pool.queue(async (workerTask) => {
      try {
        const data = (await workerTask(request)) as Uint8Array;
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d")!;
        ctx.putImageData(
          new ImageData(Uint8ClampedArray.from(data), width, height),
          0,
          0,
        );
        resolve(canvas);
      } catch (error) {
        reject(error);
      }
    });
  });
}

/**
 * Render a thumbnail for a parcel using its saved rendering configuration
 * Returns a data URL (base64 PNG) that can be used as an image src
 */
export async function renderParcelThumbnail(
  parcel: Parcel,
  size: number = DEFAULT_THUMBNAIL_SIZE
): Promise<string> {
  const map = getMapInstance();
  if (!map) {
    throw new Error("Map not initialized");
  }

  const imageWidth = size;
  const imageHeight = Math.round(size * ASPECT_RATIO);
  const bounds = calculateBounds(parcel.re, parcel.im, parcel.zoom, imageWidth, imageHeight);
  const request = buildWorkerRequest(parcel, bounds, imageWidth, imageHeight);
  const canvas = await renderChunk(map.pool, request, imageWidth, imageHeight);

  return canvas.toDataURL("image/png");
}

/**
 * Render a parcel at high resolution for print.
 * Splits into parallel columns (like ImageSaver) to avoid memory limits.
 * Returns a PNG Blob ready for upload to Printful.
 */
export async function renderParcelHighRes(
  parcel: Parcel,
  totalWidth: number,
  totalHeight: number,
): Promise<Blob> {
  const map = getMapInstance();
  if (!map) {
    throw new Error("Map not initialized");
  }

  const bounds = calculateBounds(parcel.re, parcel.im, parcel.zoom, totalWidth, totalHeight);

  // For small images, render in one pass
  if (totalWidth <= COLUMN_SPLIT_THRESHOLD) {
    const request = buildWorkerRequest(parcel, bounds, totalWidth, totalHeight);
    const canvas = await renderChunk(map.pool, request, totalWidth, totalHeight);
    return canvasToBlob(canvas);
  }

  // Split into columns for parallel rendering
  const columnWidth = Math.ceil(totalWidth / NUM_COLUMNS);
  const reDiff = bounds.reMax - bounds.reMin;
  const reDiffPerColumn = reDiff * (columnWidth / totalWidth);

  const columnPromises: Promise<HTMLCanvasElement>[] = [];
  for (let i = 0; i < NUM_COLUMNS; i++) {
    const colBounds: ComplexBounds = {
      ...bounds,
      reMin: bounds.reMin + reDiffPerColumn * i,
      reMax: bounds.reMin + reDiffPerColumn * (i + 1),
    };
    const request = buildWorkerRequest(parcel, colBounds, columnWidth, totalHeight);
    columnPromises.push(renderChunk(map.pool, request, columnWidth, totalHeight));
  }

  const columns = await Promise.all(columnPromises);

  // Combine columns onto final canvas
  const finalCanvas = document.createElement("canvas");
  finalCanvas.width = totalWidth;
  finalCanvas.height = totalHeight;
  const ctx = finalCanvas.getContext("2d")!;
  let xOffset = 0;
  for (const col of columns) {
    ctx.drawImage(col, xOffset, 0);
    xOffset += col.width;
  }

  return canvasToBlob(finalCanvas);
}

export interface CompositionConfig {
  background: "transparent" | string;
  verticalAlign: "top" | "center";
}

/**
 * Render fractal at 16:9 landscape, then compose onto the full print canvas.
 * Used for products where the print area is portrait (e.g. t-shirt) so the
 * fractal isn't cropped to a narrow vertical strip.
 */
export async function renderAndCompose(
  parcel: Parcel,
  printWidth: number,
  printHeight: number,
  composition: CompositionConfig,
): Promise<Blob> {
  // Render fractal at 16:9 landscape (full print width, height derived from aspect ratio)
  const fractalWidth = printWidth;
  const fractalHeight = Math.round(printWidth * ASPECT_RATIO);
  const fractalBlob = await renderParcelHighRes(parcel, fractalWidth, fractalHeight);

  // Load rendered fractal as an image
  const fractalImg = await blobToImage(fractalBlob);

  // Create final canvas at full print dimensions
  const canvas = document.createElement("canvas");
  canvas.width = printWidth;
  canvas.height = printHeight;
  const ctx = canvas.getContext("2d")!;

  // Fill background
  if (composition.background === "transparent") {
    ctx.clearRect(0, 0, printWidth, printHeight);
  } else {
    ctx.fillStyle = composition.background;
    ctx.fillRect(0, 0, printWidth, printHeight);
  }

  // Position fractal
  const yOffset = composition.verticalAlign === "top"
    ? 0
    : Math.round((printHeight - fractalHeight) / 2);
  ctx.drawImage(fractalImg, 0, yOffset);

  return canvasToBlob(canvas);
}

function blobToImage(blob: Blob): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Failed to load image from blob"));
    };
    img.src = url;
  });
}

function canvasToBlob(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error("toBlob failed"))),
      "image/png",
    );
  });
}

/**
 * Render thumbnails for multiple parcels in parallel
 * Returns a Map of parcelId -> dataURL
 */
export async function renderMultipleThumbnails(
  parcels: Parcel[],
  size: number = DEFAULT_THUMBNAIL_SIZE
): Promise<Map<string, string>> {
  const results = new Map<string, string>();

  // Render all thumbnails in parallel (worker pool handles concurrency)
  const promises = parcels.map(async (parcel) => {
    try {
      const dataUrl = await renderParcelThumbnail(parcel, size);
      results.set(parcel.id, dataUrl);
    } catch (error) {
      console.error(`Failed to render thumbnail for parcel ${parcel.id}:`, error);
      // Set empty string for failed renders
      results.set(parcel.id, "");
    }
  });

  await Promise.all(promises);
  return results;
}

// Simple in-memory cache for thumbnails
const thumbnailCache = new Map<string, string>();

/**
 * Get a cache key for a parcel based on its visual properties
 */
function getCacheKey(parcel: Parcel, size: number): string {
  return `${parcel.id}_${size}_${parcel.iterations}_${parcel.colorScheme}_${parcel.reverseColors}`;
}

/**
 * Render a thumbnail with caching
 * Returns cached version if available, otherwise renders and caches
 */
export async function renderParcelThumbnailCached(
  parcel: Parcel,
  size: number = DEFAULT_THUMBNAIL_SIZE
): Promise<string> {
  const cacheKey = getCacheKey(parcel, size);

  // Check cache first
  const cached = thumbnailCache.get(cacheKey);
  if (cached) {
    return cached;
  }

  // Render and cache
  const dataUrl = await renderParcelThumbnail(parcel, size);
  thumbnailCache.set(cacheKey, dataUrl);

  return dataUrl;
}

/**
 * Clear the thumbnail cache (e.g., on logout)
 */
export function clearThumbnailCache(): void {
  thumbnailCache.clear();
}
